/**
 * Integrations Engine API Client
 *
 * Client for communicating with the integrations-engine (port 4100).
 * Handles Miro and Jira integrations.
 */

/* eslint-disable @typescript-eslint/no-explicit-any -- legacy API client; typed gradually */

import { INTEGRATIONS_ENGINE_URL as INTEGRATIONS_API_BASE } from './engineUrls';
import { isUuid } from './isUuid';

// ============================================================================
// Types
// ============================================================================

export interface MiroBoard {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  modifiedAt: string;
  viewLink: string;
}

export interface MiroStickyNote {
  id: string;
  content: string;
  color: string;
  createdBy?: {
    id: string;
    name: string;
  };
  modifiedAt?: string;
  position?: {
    x: number;
    y: number;
  };
}

export interface MiroImportPreview {
  boardId: string;
  boardName: string;
  totalItems: number;
  items: Array<{
    id: string;
    content: string;
    color: string;
    suggestedCategory: string;
    author?: string;
  }>;
  colorDistribution: Record<string, number>;
}

export interface MiroImportResult {
  success: boolean;
  created: number;
  failed: number;
  errors: string[];
  /** As the engine sends them (PlatformItem: the sticky text is `label`, not `title`). */
  items: Array<{
    id: string;
    /** Sticky text, cut to 100 characters. */
    label?: string;
    title?: string;
    /** The full sticky text, only when it is longer than 100 characters. */
    description?: string;
    category?: string;
    source?: 'miro';
    sourceConnector?: 'miro';
    externalId: string;
    externalUrl?: string;
    /** `tags`: the sticky's Miro tag titles (absent when the board's tags could not be read). */
    attributes?: { color?: string; author?: string; board_name?: string; tags?: string[] } & Record<string, unknown>;
  }>;
}

export interface JiraProject {
  id: string;
  key: string;
  name: string;
  projectTypeKey: string;
}

export interface JiraExportPreview {
  projectKey: string;
  projectName: string;
  totalItems: number;
  items: Array<{
    itemId: string;
    summary: string;
    issueType: string;
    priority?: string;
    labels: string[];
  }>;
}

export interface JiraExportResult {
  success: boolean;
  summary: {
    total: number;
    successful: number;
    failed: number;
  };
  created: Array<{
    itemId: string;
    jiraKey: string;
    jiraUrl: string;
    summary: string;
  }>;
  failed: Array<{
    itemId: string;
    error: string;
  }>;
}

export interface ConnectionStatus {
  connected: boolean;
  error?: string;
}

// ============================================================================
// Notion Types
// ============================================================================

export interface NotionDatabaseSummary {
  id: string;
  databaseId?: string;
  title: string;
  description?: string;
  url: string;
  properties: Record<string, { id: string; name: string; type: string }>;
}

export interface NotionImportedItem {
  externalId: string;
  externalUrl?: string;
  title: string;
  description?: string;
  source: 'notion';
  metadata: Record<string, unknown>;
}

export interface NotionDocumentSummary {
  id: string;
  name: string;
  url: string;
  modifiedAt?: string;
}

// ============================================================================
// Miro Template Types
// ============================================================================

export interface MiroTemplate {
  id: string;
  toolId: string;
  name: string;
  description: string;
  zoneCount: number;
  zones: Array<{
    id: string;
    name: string;
    description: string;
  }>;
}

/** What the engine found on a Miro board before reading zones off it. */
export interface MiroBoardContents {
  /** Sticky notes on the whole board, before any zone filtering. */
  stickyCount: number;
  /** Titles of the board's frames. */
  frameTitles: string[];
  /** Things on the board that are neither sticky notes nor frames. */
  otherDrawnItems: number;
  /** False when nothing on the board could be the template. */
  templateFound: boolean;
}

export interface MiroTemplateImportPreview {
  templateId: string;
  templateName: string;
  boardId: string;
  totalItems: number;
  /** Absent from an engine that does not report it. */
  board?: MiroBoardContents;
  zoneSummary: Array<{
    zoneId: string;
    zoneName: string;
    itemCount: number;
    metadata: Record<string, any>;
  }>;
  sampleItems: Array<{
    id: string;
    content: string;
    zone: string;
    confidence: 'exact' | 'overlap' | 'nearest' | 'none';
  }>;
}

export interface MiroTemplateImportResult {
  success: boolean;
  templateId: string;
  templateName: string;
  boardId: string;
  totalItems: number;
  /** Absent from an engine that does not report it. */
  board?: MiroBoardContents;
  zoneSummary: Array<{
    zoneId: string;
    zoneName: string;
    itemCount: number;
    metadata: Record<string, any>;
  }>;
  items: Array<{
    id: string;
    content: string;
    position: { x: number; y: number };
    zone: {
      id: string | null;
      name: string | null;
      confidence: 'exact' | 'overlap' | 'nearest' | 'none';
    };
    toolAttributes: Record<string, any>;
    /** Zone metadata before tool translation; `frameTitle` when the sticky sits in a frame. */
    rawMetadata?: Record<string, any>;
    /** Miro sticky style; `fillColor` is a Miro palette name such as 'light_yellow'. */
    style?: { fillColor?: string };
    author?: string;
    /** The sticky's Miro tag titles (absent when the board's tags could not be read). */
    tags?: string[];
  }>;
  warnings: string[];
}

export interface MiroTemplateExportPreview {
  templateId: string;
  templateName: string;
  totalItems: number;
  zoneSummary: Array<{
    zoneId: string;
    zoneName: string;
    itemCount: number;
  }>;
  placements: Array<{
    priorityId: string;
    title: string;
    zoneId: string | null;
    zoneName: string | null;
    position: { x: number; y: number } | null;
  }>;
}

export interface MiroTemplateExportResult {
  success: boolean;
  templateId: string;
  boardId: string;
  boardUrl?: string;
  itemsCreated: number;
  itemsUpdated: number;
  itemsSkipped: number;
  duplicatesFound: number;
  placements: Array<{
    priorityId: string;
    miroItemId: string;
    zoneId: string;
    position: { x: number; y: number };
    action: 'created' | 'updated' | 'skipped';
  }>;
  warnings: string[];
}

// ============================================================================
// Field Mapping Types
// ============================================================================

export type FieldType = 
  | 'string' 
  | 'number' 
  | 'boolean' 
  | 'date' 
  | 'array' 
  | 'object'
  | 'select'
  | 'multiselect'
  | 'user'
  | 'rich_text'
  | 'url';

export interface FieldDefinition {
  id: string;
  name: string;
  path: string;
  type: FieldType;
  required?: boolean;
  description?: string;
  options?: string[];
  system: 'miro' | 'jira' | 'internal';
  category: 'core' | 'metadata' | 'custom' | 'computed';
}

export interface ValueMapping {
  sourceValue: string | number | boolean;
  targetValue: string | number | boolean;
}

export interface FieldMapping {
  id: string;
  sourceField: string;
  targetField: string;
  direction: 'import' | 'export' | 'bidirectional';
  transform?: {
    type: 'none' | 'lowercase' | 'uppercase' | 'trim' | 'parse_html' | 'to_html' | 'value_map' | 'custom';
    valueMap?: ValueMapping[];
    customExpression?: string;
  };
  defaultValue?: string | number | boolean | null;
  skipIf?: {
    condition: 'null' | 'empty' | 'equals';
    value?: string | number;
  };
  enabled: boolean;
  isCustom: boolean;
  description?: string;
}

export interface FieldMappingConfig {
  id: string;
  name: string;
  system: 'miro' | 'jira';
  scope: 'global' | 'board' | 'project';
  scopeId?: string;
  mappings: FieldMapping[];
  unmappedFieldBehavior: 'ignore' | 'passthrough' | 'warn';
  conflictResolution: 'source_wins' | 'target_wins' | 'manual';
  createdAt: string;
  updatedAt: string;
}

export interface MappingPreview {
  sourceData: Record<string, any>;
  mappedData: Record<string, any>;
  unmappedFields: string[];
  warnings: string[];
  errors: string[];
}

// ============================================================================
// Figma Types
// ============================================================================

export interface FigmaProject {
  id: string;
  name: string;
}

export interface FigmaFile {
  key: string;
  name: string;
  lastModified: string;
  thumbnailUrl?: string;
  url: string;
}

export interface FigmaPage {
  id: string;
  name: string;
  type: 'CANVAS';
}

export interface FigmaFrame {
  id: string;
  name: string;
  description?: string;
  pageId: string;
  pageName: string;
  url: string;
  thumbnailUrl?: string;
}

export interface FigmaImportItem {
  externalId: string;
  title: string;
  description?: string;
  source: 'figma';
  metadata: {
    figmaFileKey: string;
    pageId: string;
    pageName: string;
    url: string;
  };
}

export interface FigmaConnectionStatus {
  connected: boolean;
  connectedAt?: string;
  teamId?: string;
}

// ============================================================================
// Content Data Source Types (Dropbox, Google Docs) — for AI access
// ============================================================================

export interface KnowledgeConnectionStatus {
  connected: boolean;
  configured?: boolean;
  connectedAt?: string;
  /** Dropbox account display name or Google account email, when available. */
  accountName?: string;
  accountEmail?: string;
}

export interface DropboxFile {
  id: string;
  name: string;
  path: string;
  type: 'file' | 'folder';
  size?: number;
  isExportable: boolean;
  modifiedAt?: string;
}

export interface GoogleDocSummary {
  id: string;
  name: string;
  modifiedAt?: string;
  url?: string;
}

export interface DocumentContent {
  id: string;
  name: string;
  text: string;
}

// ============================================================================
// Granola Types
// ============================================================================

export interface GranolaNote {
  id: string;
  title: string;
  createdAt: string;
  updatedAt?: string;
  summary?: string;
  owner?: {
    id: string;
    name?: string;
    email?: string;
  };
  url?: string;
}

export interface GranolaConnectionStatus {
  connected: boolean;
  connectedAt?: string;
}

// ============================================================================
// Intercom Types (customer support conversations)
// ============================================================================

export type IntercomRegion = 'us' | 'eu' | 'au';

export interface IntercomConversation {
  id: string;
  title: string;
  state?: string;
  priority?: string;
  createdAt: string;
  updatedAt?: string;
  snippet?: string;
  rating?: number;
  tags: string[];
  contact?: {
    id?: string;
    name?: string;
    email?: string;
  };
  url?: string;
}

export interface IntercomConnectionStatus {
  connected: boolean;
  connectedAt?: string;
  workspaceName?: string;
  region?: string;
  /** Whether the engine has Intercom OAuth client credentials configured. */
  configured?: boolean;
}

// ============================================================================
// Zendesk Types (customer support tickets)
// ============================================================================

export interface ZendeskTicket {
  id: string;
  title: string;
  description?: string;
  status?: string;
  priority?: string;
  createdAt: string;
  updatedAt?: string;
  tags: string[];
  satisfaction?: string;
  requesterId?: string;
  url?: string;
}

export interface ZendeskConnectionStatus {
  connected: boolean;
  connectedAt?: string;
  subdomain?: string;
  userName?: string;
  /** Whether the engine has Zendesk OAuth client credentials configured. */
  configured?: boolean;
}

// ============================================================================
// Salesforce Types (CRM opportunities — customer demand / revenue-at-risk)
// ============================================================================

export interface SalesforceOpportunity {
  id: string;
  title: string;
  description?: string;
  stage?: string;
  amount?: number;
  currency?: string;
  closeDate?: string;
  probability?: number;
  accountId?: string;
  accountName?: string;
  featureRequest?: string;
  createdAt?: string;
  updatedAt?: string;
  url?: string;
}

export interface SalesforceConnectionStatus {
  connected: boolean;
  connectedAt?: string;
  instanceUrl?: string;
  /** Whether the engine has Salesforce OAuth client credentials configured. */
  configured?: boolean;
}

// ============================================================================
// Quantive Types
// ============================================================================

export interface QuantiveSession {
  id: string;
  title: string;
  start: string;
  end: string;
  status: string;
}

export interface QuantiveOkrItem {
  type: 'objective' | 'key_result';
  id: string;
  name: string;
  description?: string;
  parentName?: string;
  url: string;
}

export interface QuantiveConnectionStatus {
  connected: boolean;
  connectedAt?: string;
  accountId?: string;
}

// ============================================================================
// Datadog Types
// ============================================================================

export interface DatadogConnectionStatus {
  connected: boolean;
  connectedAt?: string;
  site?: string;
}

export interface DatadogSloStatus {
  id: string;
  name: string;
  timeframe: string;
  target: number;
  sliValue: number | null;
  meetingTarget: boolean | null;
}

export interface DatadogIncident {
  id: string;
  title: string;
  state?: string;
  severity?: string;
  createdAt?: string;
  modifiedAt?: string;
  url?: string;
}

export interface DatadogReliabilitySignals {
  slos: DatadogSloStatus[];
  incidents: {
    total: number;
    active: number;
    recent: DatadogIncident[];
  };
  monitors: {
    total: number;
    alerting: number;
    warning: number;
  };
  fetchedAt: string;
}

// ============================================================================
// Snowflake Types
// ============================================================================

export interface SnowflakeConnectionStatus {
  connected: boolean;
  connectedAt?: string;
  account?: string;
  warehouse?: string;
  database?: string;
}

export interface SnowflakeQueryResult {
  columns: Array<{ name: string; type: string }>;
  rows: Array<Record<string, unknown>>;
  rowCount: number;
  statementHandle?: string;
}

// ============================================================================
// Slack Types
// ============================================================================

export interface SlackChannel {
  id: string;
  name: string;
  isPrivate: boolean;
  memberCount?: number;
}

export type SlackEventType = 
  | 'idea_created'
  | 'idea_updated'
  | 'idea_status_changed'
  | 'idea_prioritized'
  | 'idea_added_to_focus_list'
  | 'idea_removed_from_focus_list'
  | 'comment_added'
  | 'priority_changed'
  | 'focus_list_published'
  | 'cycle_started'
  | 'cycle_completed'
  | 'task_assigned';

export interface SlackChannelConfig {
  id: string;
  workspaceId: string;
  channelId: string;
  channelName: string;
  scope: 'organization' | 'team';
  teamId?: string;
  teamName?: string;
  publishEvents: SlackEventType[];
  syncComments: boolean;
  syncNewIdeas: boolean;
  allowIdeaCreation: boolean;
  includeDescription: boolean;
  includeMetadata: boolean;
  mentionSubmitter: boolean;
  enabled: boolean;
  lastSyncAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SlackEventTypeInfo {
  type: SlackEventType;
  name: string;
  description: string;
  emoji: string;
  defaultEnabled: boolean;
}

export interface SlackConnectionStatus {
  connected: boolean;
  slackTeamId?: string;
  slackTeamName?: string;
  enabled?: boolean;
  connectedAt?: string;
  channelCount?: number;
  /** Effective notify channel (personal preference, else workspace default) */
  notifyChannelId?: string | null;
  notifyChannelName?: string | null;
  notifyChannelSource?: 'user' | 'workspace' | null;
  workspaceNotifyChannelId?: string | null;
  workspaceNotifyChannelName?: string | null;
}

export interface SlackPublishResult {
  success: boolean;
  messagesSent: number;
  errors: Array<{ channelId: string; error: string }>;
}

// ============================================================================
// Zoom Types
// ============================================================================

export interface ZoomConnectionStatus {
  connected: boolean;
  accountEmail?: string;
  accountName?: string;
  connectedAt?: string;
}

export interface ZoomMeeting {
  id: string;
  topic: string;
  startTime: string;
  duration: number;
  joinUrl: string;
  startUrl: string;
  password?: string;
  hostEmail: string;
}

export interface ZoomMeetingCreateParams {
  taskId: string;
  taskName: string;
  toolName: string;
  description?: string;
  scheduledStart: Date;
  durationMinutes?: number;
  participantEmails?: string[];
  waitingRoom?: boolean;
  autoRecord?: boolean;
}

export interface ZoomMeetingResult {
  success: boolean;
  meetingId?: string;
  joinUrl?: string;
  startUrl?: string;
  password?: string;
  error?: string;
}

// ── Zoom transcript / chat ingestion ─────────────────────────────────────────

export interface ZoomRecordingSummary {
  meetingId: string;
  uuid: string;
  topic: string;
  startTime: string;
  duration: number;
  hostEmail?: string;
  hasTranscript: boolean;
  hasChat: boolean;
  shareUrl?: string;
}

export interface MeetingTranscriptCue {
  index: number;
  start: string;
  end: string;
  speaker?: string;
  text: string;
}

export interface MeetingTranscript {
  cues: MeetingTranscriptCue[];
  text: string;
}

export interface MeetingChatMessage {
  id?: string;
  timestamp?: string;
  sender: string;
  text: string;
}

export interface MeetingImportedItem {
  id: string;
  label: string;
  description?: string;
  sourceConnector: 'zoom' | 'microsoft-teams' | 'google-meet';
  externalId: string;
  externalUrl?: string;
  importedAt: string;
  attributes: Record<string, string | number | boolean | null>;
}

export interface MeetingImportResult {
  success: boolean;
  meeting?: { id: string; topic?: string; subject?: string; startTime?: string };
  conference?: { id: string; startTime?: string };
  itemCount: number;
  items: MeetingImportedItem[];
}

export interface GoogleMeetConferenceRecord {
  conferenceRecordId: string;
  name: string;
  spaceName?: string;
  startTime?: string;
  endTime?: string;
}

// ============================================================================
// Microsoft Teams Types
// ============================================================================

export interface MicrosoftTeamsConnectionStatus {
  connected: boolean;
  accountEmail?: string;
  accountName?: string;
  tenantId?: string;
  connectedAt?: string;
}

export interface TeamsMeetingTranscriptRef {
  transcriptId: string;
  meetingId: string;
  createdDateTime: string;
  meetingOrganizerId?: string;
  subject?: string;
}

// ============================================================================
// Google Meet Types
// ============================================================================

export interface GoogleMeetConnectionStatus {
  connected: boolean;
  accountEmail?: string;
  connectedAt?: string;
  calendarId?: string;
}

export interface GoogleMeetMeetingCreateParams {
  taskId: string;
  taskName: string;
  toolName: string;
  description?: string;
  scheduledStart: Date;
  durationMinutes?: number;
  participantEmails?: string[];
  calendarId?: string;
}

export interface GoogleMeetMeetingResult {
  success: boolean;
  meetingUrl?: string;
  eventId?: string;
  eventLink?: string;
  error?: string;
}

// ============================================================================
// API Client
// ============================================================================

/**
 * Normalize browser fetch errors ("Load failed", "Failed to fetch") for every
 * caller (EntitySyncPanel, IntegrationSyncPanel, settings, etc.).
 */
export function formatIntegrationsNetworkError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  if (/load failed|failed to fetch|networkerror|network request failed|aborted/i.test(raw)) {
    return (
      'Could not reach the integrations connector service. ' +
      'Confirm Miro in Settings → Connectors, and that the integrations engine is running ' +
      '(or set VITE_INTEGRATIONS_API_URL — see src/lib/envConfig.ts).'
    );
  }
  return raw;
}

/** Combine engine `error` + `details` so Safari's opaque "500 (connect)" is not the only signal. */
export function formatIntegrationsHttpError(
  status: number,
  data: { error?: string; details?: unknown },
): string {
  const details = typeof data.details === 'string' && data.details.trim() ? data.details : undefined;
  const msg = data.error || `Request failed with status ${status}`;
  return details && details !== msg ? `${msg}: ${details}` : msg;
}

class IntegrationsApiClient {
  private baseUrl: string;
  private workspaceId: string;

  constructor(workspaceId: string = 'default') {
    this.baseUrl = INTEGRATIONS_API_BASE;
    this.workspaceId = workspaceId;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: any,
    queryParams?: Record<string, string>
  ): Promise<T> {
    const url = new URL(`${this.baseUrl}${path}`);
    
    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    let response: Response;
    try {
      response = await fetch(url.toString(), options);
    } catch (e) {
      throw new Error(formatIntegrationsNetworkError(e));
    }

    const text = await response.text();
    let data: { error?: string } & Record<string, unknown>;
    try {
      data = text ? (JSON.parse(text) as typeof data) : {};
    } catch {
      throw new Error(
        response.ok
          ? 'Integrations service returned a non-JSON response.'
          : `Integrations service error (${response.status}).`
      );
    }

    if (!response.ok) {
      const details = typeof data.details === 'string' && data.details.trim() ? data.details : undefined;
      // Safari only shows "Failed to load resource: 500 (connect, line 0)" —
      // log the real engine message so console paste is diagnosable.
      console.warn(`[integrationsApi] ${method} ${path} ${response.status}`, {
        error: data.error,
        details,
      });
      throw new Error(formatIntegrationsHttpError(response.status, data));
    }

    return data as T;
  }

  // ==========================================================================
  // Health Check
  // ==========================================================================

  async checkHealth(): Promise<{ status: string; integrations: Record<string, any> }> {
    return this.request('GET', '/health');
  }

  async getStatus(): Promise<any> {
    return this.request('GET', '/api/status');
  }

  // ==========================================================================
  // Notion Integration
  //
  // OAuth is the primary connect path: connectNotion() with no token returns an
  // { authUrl } to redirect to. Passing an internal integration token connects
  // directly (local / single-user). Import/export map databases <-> priorities;
  // listDocuments/readDocument expose pages as an AI-readable content source.
  // ==========================================================================

  async connectNotion(
    opts?: { accessToken?: string; redirectUrl?: string; userId?: string; scope?: 'workspace' | 'user' },
  ): Promise<{ success: boolean; authUrl?: string; error?: string }> {
    return this.request('POST', '/notion/connect', {
      workspaceId: this.workspaceId,
      accessToken: opts?.accessToken || undefined,
      redirectUrl: opts?.redirectUrl,
      userId: opts?.userId,
      scope: opts?.scope,
    });
  }

  async getNotionStatus(userId?: string): Promise<{
    success: boolean;
    connected: boolean;
    tokenType?: 'oauth' | 'internal';
    workspaceName?: string;
    oauthConfigured?: boolean;
  }> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (userId) params.userId = userId;
    return this.request('GET', '/notion/status', undefined, params);
  }

  async disconnectNotion(userId?: string): Promise<void> {
    await this.request('DELETE', '/notion/disconnect', { workspaceId: this.workspaceId, userId });
  }

  async listNotionDatabases(userId?: string): Promise<NotionDatabaseSummary[]> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (userId) params.userId = userId;
    const result = await this.request<{ success: boolean; databases: NotionDatabaseSummary[] }>(
      'GET',
      '/notion/databases',
      undefined,
      params,
    );
    return result.databases || [];
  }

  async importFromNotion(
    databaseId: string,
    opts?: { titleProperty?: string; descriptionProperty?: string; userId?: string },
  ): Promise<{ success: boolean; items: NotionImportedItem[]; count: number }> {
    return this.request('POST', '/notion/import', {
      workspaceId: this.workspaceId,
      databaseId,
      titleProperty: opts?.titleProperty,
      descriptionProperty: opts?.descriptionProperty,
      userId: opts?.userId,
    });
  }

  async exportToNotion(
    databaseId: string,
    priorities: Array<{
      id: string;
      title: string;
      description?: string;
      rank?: number;
      score?: number;
      category?: string;
    }>,
    opts?: { userId?: string },
  ): Promise<{
    success: boolean;
    created: Array<{ priorityId: string; notionId: string; url: string }>;
    errors: Array<{ priorityId: string; error: string }>;
  }> {
    return this.request('POST', '/notion/export', {
      workspaceId: this.workspaceId,
      databaseId,
      priorities,
      userId: opts?.userId,
    });
  }

  async listNotionDocuments(
    query?: string,
    opts?: { limit?: number; userId?: string },
  ): Promise<NotionDocumentSummary[]> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (query) params.query = query;
    if (opts?.limit) params.limit = String(opts.limit);
    if (opts?.userId) params.userId = opts.userId;
    const result = await this.request<{ success: boolean; documents: NotionDocumentSummary[] }>(
      'GET',
      '/notion/documents',
      undefined,
      params,
    );
    return result.documents || [];
  }

  async readNotionDocument(
    documentId: string,
    userId?: string,
  ): Promise<{ success: boolean; id: string; name: string; text: string }> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (userId) params.userId = userId;
    return this.request('GET', `/notion/documents/${encodeURIComponent(documentId)}`, undefined, params);
  }

  // ==========================================================================
  // Miro Integration
  // ==========================================================================

  async connectMiro(accessToken: string, teamId?: string): Promise<ConnectionStatus> {
    try {
      const result = await this.request<{ success: boolean; message?: string; error?: string }>(
        'POST',
        '/miro/connect',
        {
          workspaceId: this.workspaceId,
          accessToken,
          teamId,
        }
      );
      return { connected: result.success, error: result.error };
    } catch (error) {
      return { connected: false, error: error instanceof Error ? error.message : 'Connection failed' };
    }
  }

  async disconnectMiro(): Promise<void> {
    await this.request('DELETE', '/miro/disconnect', { workspaceId: this.workspaceId });
  }

  async listMiroBoards(): Promise<MiroBoard[]> {
    const result = await this.request<{ success: boolean; boards: MiroBoard[] }>(
      'GET',
      '/miro/boards',
      undefined,
      { workspaceId: this.workspaceId }
    );
    return result.boards || [];
  }

  async getMiroBoard(boardId: string): Promise<MiroBoard> {
    const result = await this.request<{ success: boolean; board: MiroBoard }>(
      'GET',
      `/miro/boards/${boardId}`,
      undefined,
      { workspaceId: this.workspaceId }
    );
    return result.board;
  }

  async previewMiroImport(
    boardId: string,
    filters?: { colors?: string[]; authorIds?: string[] },
    mapping?: { colorToCategory?: Record<string, string>; positionToOrder?: boolean }
  ): Promise<MiroImportPreview> {
    const result = await this.request<{ success: boolean; preview: MiroImportPreview }>(
      'POST',
      '/miro/preview',
      {
        workspaceId: this.workspaceId,
        boardId,
        filters,
        mapping,
      }
    );
    return result.preview;
  }

  async importFromMiro(
    boardId: string,
    options?: {
      filters?: { colors?: string[]; authorIds?: string[] };
      mapping?: { colorToCategory?: Record<string, string>; positionToOrder?: boolean };
      targetSessionId?: string;
    }
  ): Promise<MiroImportResult> {
    const result = await this.request<{ success: boolean; result: any; items: any[] }>(
      'POST',
      '/miro/import',
      {
        workspaceId: this.workspaceId,
        boardId,
        filters: options?.filters,
        mapping: options?.mapping,
        targetSessionId: options?.targetSessionId,
      }
    );
    return {
      success: result.success,
      created: result.result?.created || 0,
      failed: result.result?.failed || 0,
      errors: result.result?.errors || [],
      items: result.items || [],
    };
  }

  async getMiroColorMappings(): Promise<Record<string, string>> {
    const result = await this.request<{ success: boolean; mappings: Record<string, string> }>(
      'GET',
      '/miro/color-mappings'
    );
    return result.mappings || {};
  }

  // ==========================================================================
  // Miro Templates
  // ==========================================================================

  /**
   * List available Miro templates for prioritization tools
   */
  async listMiroTemplates(): Promise<MiroTemplate[]> {
    const result = await this.request<{ success: boolean; templates: MiroTemplate[] }>(
      'GET',
      '/miro/templates'
    );
    return result.templates || [];
  }

  /**
   * Get detailed template information
   */
  async getMiroTemplate(templateId: string): Promise<MiroTemplate & { 
    canvasBounds: { x: number; y: number; width: number; height: number };
    defaultStickySize: { width: number; height: number };
    zones: Array<{
      id: string;
      name: string;
      description: string;
      bounds: { x: number; y: number; width: number; height: number };
      metadata: Record<string, any>;
      style?: { backgroundColor?: string };
    }>;
  }> {
    const result = await this.request<{ success: boolean; template: any }>(
      'GET',
      `/miro/templates/${templateId}`
    );
    return result.template;
  }

  /**
   * Get template for a specific prioritization tool
   */
  async getMiroTemplateByToolId(toolId: string): Promise<MiroTemplate | null> {
    try {
      const result = await this.request<{ success: boolean; template: MiroTemplate }>(
        'GET',
        `/miro/templates/by-tool/${toolId}`
      );
      return result.template || null;
    } catch {
      return null;
    }
  }

  /**
   * Preview template-based import from Miro
   */
  async previewMiroTemplateImport(
    boardId: string,
    templateId: string,
    options?: {
      filterToTemplateBounds?: boolean;
      minConfidence?: 'exact' | 'overlap' | 'nearest';
      includeUnzoned?: boolean;
    }
  ): Promise<MiroTemplateImportPreview> {
    const result = await this.request<{ success: boolean; preview: MiroTemplateImportPreview }>(
      'POST',
      '/miro/templates/preview-import',
      {
        workspaceId: this.workspaceId,
        boardId,
        templateId,
        ...options,
      }
    );
    return result.preview;
  }

  /**
   * Import from Miro using template-based zone detection
   */
  async importFromMiroTemplate(
    boardId: string,
    templateId: string,
    options?: {
      filterToTemplateBounds?: boolean;
      minConfidence?: 'exact' | 'overlap' | 'nearest';
      includeUnzoned?: boolean;
    }
  ): Promise<MiroTemplateImportResult> {
    const result = await this.request<MiroTemplateImportResult>(
      'POST',
      '/miro/templates/import',
      {
        workspaceId: this.workspaceId,
        boardId,
        templateId,
        ...options,
      }
    );
    return result;
  }

  /**
   * Preview template-based export to Miro
   */
  async previewMiroTemplateExport(
    templateId: string,
    priorities: Array<{
      id: string;
      title: string;
      description?: string;
      attributes?: Record<string, any>;
      metadata?: Record<string, any>;
    }>,
    toolId?: string
  ): Promise<MiroTemplateExportPreview> {
    const result = await this.request<{ success: boolean; preview: MiroTemplateExportPreview }>(
      'POST',
      '/miro/templates/preview-export',
      {
        templateId,
        toolId,
        priorities,
      }
    );
    return result.preview;
  }

  /**
   * Export to Miro using template-based placement
   */
  async exportToMiroTemplate(
    templateId: string,
    priorities: Array<{
      id: string;
      title: string;
      description?: string;
      attributes?: Record<string, any>;
      metadata?: Record<string, any>;
      color?: string;
    }>,
    options?: {
      boardId?: string;
      toolId?: string;
      createBoard?: boolean;
      newBoardName?: string;
      clearExisting?: boolean;
      /** How to handle duplicates: 'skip' (default), 'update', or 'create' */
      duplicateHandling?: 'skip' | 'update' | 'create';
      /** Custom category names to use for frame titles instead of template defaults */
      customCategories?: string[];
    }
  ): Promise<MiroTemplateExportResult> {
    const result = await this.request<MiroTemplateExportResult>(
      'POST',
      '/miro/templates/export',
      {
        workspaceId: this.workspaceId,
        templateId,
        priorities,
        ...options,
      }
    );
    return result;
  }

  /**
   * Export a flat list of items to a new Miro board as sticky notes.
   * Returns the new board's URL so the user can open it directly.
   */
  async exportItemsToMiro(
    boardName: string,
    items: Array<{ title: string; description?: string }>
  ): Promise<{ boardId: string; boardUrl: string; count: number }> {
    const result = await this.request<{ success: boolean; boardId: string; boardUrl: string; count: number }>(
      'POST',
      '/miro/export-items',
      {
        workspaceId: this.workspaceId,
        boardName,
        items,
      }
    );
    return { boardId: result.boardId, boardUrl: result.boardUrl, count: result.count };
  }

  // ==========================================================================
  // Jira Integration
  // ==========================================================================

  async connectJira(siteUrl: string, email: string, apiToken: string): Promise<ConnectionStatus> {
    try {
      const result = await this.request<{ success: boolean; message?: string; error?: string }>(
        'POST',
        '/jira/connect',
        {
          workspaceId: this.workspaceId,
          siteUrl,
          email,
          apiToken,
        }
      );
      return { connected: result.success, error: result.error };
    } catch (error) {
      return { connected: false, error: error instanceof Error ? error.message : 'Connection failed' };
    }
  }

  async disconnectJira(): Promise<void> {
    await this.request('DELETE', '/jira/disconnect', { workspaceId: this.workspaceId });
  }

  async listJiraProjects(): Promise<JiraProject[]> {
    const result = await this.request<{ success: boolean; projects: JiraProject[] }>(
      'GET',
      '/jira/projects',
      undefined,
      { workspaceId: this.workspaceId }
    );
    return result.projects || [];
  }

  async getJiraProject(projectKey: string): Promise<JiraProject> {
    const result = await this.request<{ success: boolean; project: JiraProject }>(
      'GET',
      `/jira/projects/${projectKey}`,
      undefined,
      { workspaceId: this.workspaceId }
    );
    return result.project;
  }

  async previewJiraExport(
    projectKey: string,
    priorities: Array<{
      id: string;
      title: string;
      description?: string;
      rank: number;
      score?: number;
      category?: string;
    }>,
    options?: {
      labels?: string[];
      includeDescription?: boolean;
      includeRank?: boolean;
      includeScore?: boolean;
    }
  ): Promise<JiraExportPreview> {
    const result = await this.request<{ success: boolean; preview: JiraExportPreview }>(
      'POST',
      '/jira/preview',
      {
        workspaceId: this.workspaceId,
        projectKey,
        priorities: priorities.map(p => ({
          id: p.id,
          label: p.title,
          description: p.description,
          rank: p.rank,
          score: p.score,
          category: p.category,
        })),
        options,
      }
    );
    return result.preview;
  }

  async exportToJira(
    projectKey: string,
    priorities: Array<{
      id: string;
      title: string;
      description?: string;
      rank: number;
      score?: number;
      category?: string;
    }>,
    options?: {
      labels?: string[];
      includeDescription?: boolean;
      includeRank?: boolean;
      includeScore?: boolean;
      sessionId?: string;
      snapshotId?: string;
    }
  ): Promise<JiraExportResult> {
    const result = await this.request<{ success: boolean; result: any }>(
      'POST',
      '/jira/export',
      {
        workspaceId: this.workspaceId,
        projectKey,
        priorities: priorities.map(p => ({
          id: p.id,
          label: p.title,
          description: p.description,
          rank: p.rank,
          score: p.score,
          category: p.category,
        })),
        options,
        sessionId: options?.sessionId,
        snapshotId: options?.snapshotId,
      }
    );
    return {
      success: result.success,
      summary: result.result?.summary || { total: 0, successful: 0, failed: 0 },
      created: result.result?.created || [],
      failed: result.result?.failed || [],
    };
  }

  async getJiraPriorityPresets(): Promise<Record<string, any>> {
    const result = await this.request<{ success: boolean; presets: Record<string, any> }>(
      'GET',
      '/jira/priority-presets'
    );
    return result.presets || {};
  }

  /**
   * Fetch the current status for a single linked Jira issue.
   * Used by the ItemJiraSyncPanel to show live status and detect completion.
   */
  async getJiraIssueStatus(issueKey: string): Promise<{
    issueKey: string;
    statusName: string;
    statusCategory: string; // 'new' | 'indeterminate' | 'done'
    isDone: boolean;
    resolution: string | null;
  }> {
    const result = await this.request<{
      success: boolean;
      status: {
        issueKey: string;
        statusName: string;
        statusCategory: string;
        isDone: boolean;
        resolution: string | null;
      };
    }>('GET', '/jira/issue-status', undefined, {
      workspaceId: this.workspaceId,
      issueKey,
    });
    return result.status;
  }

  async searchJiraIssues(
    projectKey: string,
    options?: {
      issueTypes?: string[];
      search?: string;
      maxResults?: number;
    }
  ): Promise<Array<{ id: string; key: string; summary: string; issueType: string; status: string; url: string }>> {
    const params: Record<string, string> = {
      workspaceId: this.workspaceId,
      projectKey,
    };
    if (options?.issueTypes?.length) params.issueTypes = options.issueTypes.join(',');
    if (options?.search) params.search = options.search;
    if (options?.maxResults) params.maxResults = String(options.maxResults);

    const result = await this.request<{
      success: boolean;
      issues: Array<{ id: string; key: string; summary: string; issueType: string; status: string; url: string }>;
    }>('GET', '/jira/issues', undefined, params);
    return result.issues || [];
  }

  // ==========================================================================
  // Quantive Integration
  // ==========================================================================

  async connectQuantive(apiKey: string, accountId: string): Promise<{ success: boolean; accountName?: string; error?: string }> {
    try {
      const result = await this.request<{ success: boolean; accountName?: string; error?: string }>(
        'POST',
        '/quantive/connect',
        { workspaceId: this.workspaceId, apiKey, accountId }
      );
      return result;
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Connection failed' };
    }
  }

  async disconnectQuantive(): Promise<void> {
    await this.request('DELETE', '/quantive/disconnect', { workspaceId: this.workspaceId });
  }

  async getQuantiveStatus(): Promise<QuantiveConnectionStatus> {
    const result = await this.request<QuantiveConnectionStatus & { success: boolean }>(
      'GET',
      '/quantive/status',
      undefined,
      { workspaceId: this.workspaceId }
    );
    return { connected: result.connected, connectedAt: result.connectedAt, accountId: result.accountId };
  }

  async listQuantiveSessions(): Promise<QuantiveSession[]> {
    const result = await this.request<{ success: boolean; sessions: QuantiveSession[] }>(
      'GET',
      '/quantive/sessions',
      undefined,
      { workspaceId: this.workspaceId }
    );
    return result.sessions || [];
  }

  async searchQuantiveOkrs(options?: {
    sessionId?: string;
    types?: string[];
    search?: string;
    limit?: number;
  }): Promise<QuantiveOkrItem[]> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (options?.sessionId) params.sessionId = options.sessionId;
    if (options?.types?.length) params.types = options.types.join(',');
    if (options?.search) params.search = options.search;
    if (options?.limit) params.limit = String(options.limit);

    const result = await this.request<{ success: boolean; okrs: QuantiveOkrItem[] }>(
      'GET',
      '/quantive/okrs',
      undefined,
      params
    );
    return result.okrs || [];
  }

  // ==========================================================================
  // Field Mappings
  // ==========================================================================

  /**
   * Get all available field definitions
   */
  async getFieldDefinitions(): Promise<{
    internal: FieldDefinition[];
    miro: FieldDefinition[];
    jira: FieldDefinition[];
  }> {
    const result = await this.request<{ success: boolean; fields: any }>(
      'GET',
      '/field-mappings/fields'
    );
    return result.fields || { internal: [], miro: [], jira: [] };
  }

  /**
   * Get field definitions for a specific system
   */
  async getSystemFields(system: 'miro' | 'jira' | 'internal'): Promise<FieldDefinition[]> {
    const result = await this.request<{ success: boolean; fields: FieldDefinition[] }>(
      'GET',
      `/field-mappings/fields/${system}`
    );
    return result.fields || [];
  }

  /**
   * Get default mappings for a system
   */
  async getDefaultMappings(
    system: 'miro' | 'jira',
    direction?: 'import' | 'export'
  ): Promise<{
    import?: FieldMapping[];
    export?: FieldMapping[];
  } | FieldMapping[]> {
    const path = direction
      ? `/field-mappings/defaults/${system}?direction=${direction}`
      : `/field-mappings/defaults/${system}`;
    
    const result = await this.request<{ success: boolean; mappings: any }>(
      'GET',
      path
    );
    return result.mappings || [];
  }

  /**
   * Get effective mappings (with overrides applied)
   */
  async getEffectiveMappings(
    system: 'miro' | 'jira',
    direction: 'import' | 'export',
    scopeId?: string
  ): Promise<FieldMapping[]> {
    const path = scopeId
      ? `/field-mappings/effective/${system}/${direction}?scopeId=${scopeId}`
      : `/field-mappings/effective/${system}/${direction}`;
    
    const result = await this.request<{ success: boolean; mappings: FieldMapping[] }>(
      'GET',
      path
    );
    return result.mappings || [];
  }

  /**
   * Save a custom mapping configuration
   */
  async saveMappingConfig(config: Omit<FieldMappingConfig, 'id' | 'createdAt' | 'updatedAt'>): Promise<FieldMappingConfig> {
    const result = await this.request<{ success: boolean; config: FieldMappingConfig }>(
      'POST',
      '/field-mappings/configs',
      config
    );
    return result.config;
  }

  /**
   * Preview how mappings would transform data
   */
  async previewMappings(
    system: 'miro' | 'jira',
    direction: 'import' | 'export',
    sourceData: Record<string, any>,
    scopeId?: string
  ): Promise<MappingPreview> {
    const result = await this.request<{ success: boolean; preview: MappingPreview }>(
      'POST',
      '/field-mappings/preview',
      { system, direction, sourceData, scopeId }
    );
    return result.preview;
  }

  /**
   * Transform data using field mappings
   */
  async transformData(
    system: 'miro' | 'jira',
    direction: 'import' | 'export',
    data: Record<string, any> | Record<string, any>[],
    scopeId?: string
  ): Promise<Record<string, any> | Record<string, any>[]> {
    const result = await this.request<{ success: boolean; transformed: any }>(
      'POST',
      '/field-mappings/transform',
      { system, direction, data, scopeId }
    );
    return result.transformed;
  }

  /**
   * Get suggested mappings based on field names
   */
  async suggestMappings(
    sourceSystem: 'miro' | 'jira' | 'internal',
    targetSystem: 'miro' | 'jira' | 'internal'
  ): Promise<Array<{
    sourceField: FieldDefinition;
    targetField: FieldDefinition;
    confidence: 'high' | 'medium' | 'low';
    reason: string;
  }>> {
    const result = await this.request<{ success: boolean; suggestions: any[] }>(
      'POST',
      '/field-mappings/suggest',
      { sourceSystem, targetSystem }
    );
    return result.suggestions || [];
  }

  // ==========================================================================
  // Slack Integration
  // ==========================================================================

  /**
   * Connect to Slack via OAuth v2 — returns authUrl for redirect.
   */
  async connectSlack(options?: {
    userId?: string;
    redirectUrl?: string;
  }): Promise<{
    success: boolean;
    authUrl?: string;
    error?: string;
  }> {
    try {
      return await this.request<{
        success: boolean;
        authUrl?: string;
        error?: string;
      }>('POST', '/slack/connect', {
        workspaceId: this.workspaceId,
        userId: options?.userId || 'user',
        redirectUrl:
          options?.redirectUrl ||
          (typeof window !== 'undefined' ? window.location.href : undefined),
      });
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Connection failed',
      };
    }
  }

  /**
   * Disconnect from Slack
   */
  async disconnectSlack(): Promise<void> {
    await this.request('DELETE', '/slack/disconnect', { workspaceId: this.workspaceId });
  }

  /**
   * Get Slack connection status
   */
  /**
   * Get Slack connection status.
   * Pass userId to include that user's My Connectors notify preference.
   */
  async getSlackStatus(userId?: string): Promise<SlackConnectionStatus> {
    const result = await this.request<SlackConnectionStatus & { success: boolean }>(
      'GET',
      '/slack/status',
      undefined,
      {
        workspaceId: this.workspaceId,
        ...(userId ? { userId } : {}),
      }
    );
    return {
      connected: result.connected,
      slackTeamId: result.slackTeamId,
      slackTeamName: result.slackTeamName,
      enabled: result.enabled,
      connectedAt: result.connectedAt,
      channelCount: result.channelCount,
      notifyChannelId: result.notifyChannelId ?? null,
      notifyChannelName: result.notifyChannelName ?? null,
      notifyChannelSource: result.notifyChannelSource ?? null,
      workspaceNotifyChannelId: result.workspaceNotifyChannelId ?? null,
      workspaceNotifyChannelName: result.workspaceNotifyChannelName ?? null,
    };
  }

  /**
   * Workspace default channel (admin Connectors) for ambient digests.
   */
  async setSlackNotifyChannel(channelId: string): Promise<{
    notifyChannelId: string;
    config: SlackChannelConfig;
  }> {
    const result = await this.request<{
      success: boolean;
      notifyChannelId: string;
      config: SlackChannelConfig;
    }>('POST', '/slack/channels/notify-default', {
      workspaceId: this.workspaceId,
      channelId,
    });
    return {
      notifyChannelId: result.notifyChannelId,
      config: result.config,
    };
  }

  /**
   * Personal notification channel (My Connectors).
   */
  async setSlackUserNotifyPreference(
    userId: string,
    channelId: string,
  ): Promise<{ notifyChannelId: string; notifyChannelName: string }> {
    const result = await this.request<{
      success: boolean;
      notifyChannelId: string;
      notifyChannelName: string;
    }>('POST', '/slack/channels/notify-preference', {
      workspaceId: this.workspaceId,
      userId,
      channelId,
    });
    return {
      notifyChannelId: result.notifyChannelId,
      notifyChannelName: result.notifyChannelName,
    };
  }

  /**
   * Deliver a plain Slack notification (ambient / task).
   */
  async notifySlack(options: {
    message: string;
    subject?: string;
    channelId?: string;
    userId?: string;
  }): Promise<SlackPublishResult> {
    const result = await this.request<SlackPublishResult & { success: boolean }>(
      'POST',
      '/slack/notify',
      {
        workspaceId: this.workspaceId,
        message: options.message,
        subject: options.subject,
        channelId: options.channelId,
        userId: options.userId,
      },
    );
    return {
      success: result.success,
      messagesSent: result.messagesSent,
      errors: result.errors,
    };
  }

  /**
   * List available Slack channels
   */
  async listSlackChannels(): Promise<SlackChannel[]> {
    const result = await this.request<{ success: boolean; channels: SlackChannel[] }>(
      'GET',
      '/slack/channels/available',
      undefined,
      { workspaceId: this.workspaceId }
    );
    return result.channels || [];
  }

  /**
   * List configured Slack channels
   */
  async listSlackChannelConfigs(): Promise<SlackChannelConfig[]> {
    const result = await this.request<{ success: boolean; channels: SlackChannelConfig[] }>(
      'GET',
      '/slack/channels/configured',
      undefined,
      { workspaceId: this.workspaceId }
    );
    return result.channels || [];
  }

  /**
   * Configure a Slack channel
   */
  async configureSlackChannel(
    channelId: string,
    options: {
      scope: 'organization' | 'team';
      teamId?: string;
      teamName?: string;
      publishEvents?: SlackEventType[];
      syncComments?: boolean;
      syncNewIdeas?: boolean;
      allowIdeaCreation?: boolean;
      includeDescription?: boolean;
      includeMetadata?: boolean;
      mentionSubmitter?: boolean;
    }
  ): Promise<SlackChannelConfig> {
    const result = await this.request<{ success: boolean; config: SlackChannelConfig }>(
      'POST',
      '/slack/channels/configure',
      {
        workspaceId: this.workspaceId,
        channelId,
        ...options,
      }
    );
    return result.config;
  }

  /**
   * Update a Slack channel configuration
   */
  async updateSlackChannelConfig(
    configId: string,
    updates: Partial<Omit<SlackChannelConfig, 'id' | 'workspaceId' | 'channelId' | 'createdAt'>>
  ): Promise<SlackChannelConfig> {
    const result = await this.request<{ success: boolean; config: SlackChannelConfig }>(
      'PUT',
      `/slack/channels/${configId}`,
      updates
    );
    return result.config;
  }

  /**
   * Delete a Slack channel configuration
   */
  async deleteSlackChannelConfig(configId: string): Promise<void> {
    await this.request('DELETE', `/slack/channels/${configId}`);
  }

  /**
   * Get available Slack event types
   */
  async getSlackEventTypes(): Promise<SlackEventTypeInfo[]> {
    const result = await this.request<{ success: boolean; eventTypes: SlackEventTypeInfo[] }>(
      'GET',
      '/slack/event-types'
    );
    return result.eventTypes || [];
  }

  /**
   * Publish an idea to Slack
   */
  async publishIdeaToSlack(
    idea: {
      id: string;
      title: string;
      description?: string;
      status?: string;
      category?: string;
      submittedBy?: string;
      submitterEmail?: string;
      priority?: string;
      score?: number;
      rank?: number;
      appUrl?: string;
    },
    eventType: SlackEventType,
    teamId?: string
  ): Promise<SlackPublishResult> {
    const result = await this.request<SlackPublishResult & { success: boolean }>(
      'POST',
      '/slack/publish/idea',
      {
        workspaceId: this.workspaceId,
        idea,
        eventType,
        teamId,
      }
    );
    return {
      success: result.success,
      messagesSent: result.messagesSent,
      errors: result.errors,
    };
  }

  /**
   * Publish a comment to Slack (as thread reply)
   */
  async publishCommentToSlack(
    ideaId: string,
    comment: {
      id: string;
      text: string;
      authorId: string;
      authorName: string;
      authorEmail?: string;
      createdAt: string;
    }
  ): Promise<SlackPublishResult> {
    const result = await this.request<SlackPublishResult & { success: boolean }>(
      'POST',
      '/slack/publish/comment',
      {
        workspaceId: this.workspaceId,
        ideaId,
        comment: {
          ...comment,
          source: 'app',
        },
      }
    );
    return {
      success: result.success,
      messagesSent: result.messagesSent,
      errors: result.errors,
    };
  }

  /**
   * Publish a general event to Slack
   */
  async publishEventToSlack(
    eventType: SlackEventType,
    data: Record<string, any>
  ): Promise<SlackPublishResult> {
    const result = await this.request<SlackPublishResult & { success: boolean }>(
      'POST',
      '/slack/publish/event',
      {
        workspaceId: this.workspaceId,
        eventType,
        data,
      }
    );
    return {
      success: result.success,
      messagesSent: result.messagesSent,
      errors: result.errors,
    };
  }

  /**
   * Sync comments from Slack threads
   */
  async syncSlackComments(ideaId: string): Promise<{
    comments: Array<{
      slackTs: string;
      slackUserId: string;
      text: string;
      isNew: boolean;
    }>;
    newCommentsCount: number;
  }> {
    const result = await this.request<{
      success: boolean;
      comments: any[];
      newCommentsCount: number;
    }>(
      'POST',
      '/slack/sync/comments',
      {
        workspaceId: this.workspaceId,
        ideaId,
      }
    );
    return {
      comments: result.comments || [],
      newCommentsCount: result.newCommentsCount || 0,
    };
  }

  // ==========================================================================
  // Google Calendar Integration
  // ==========================================================================

  /**
   * Initiate Google Calendar OAuth flow
   */
  async initiateGoogleCalendarAuth(params: {
    userId: string;
    redirectUrl?: string;
  }): Promise<{ success: boolean; authUrl?: string; error?: string }> {
    return this.request<{ success: boolean; authUrl?: string; error?: string }>(
      'POST',
      '/google-calendar/connect',
      {
        workspaceId: this.workspaceId,
        userId: params.userId,
        redirectUrl: params.redirectUrl,
      }
    );
  }

  /**
   * Disconnect Google Calendar
   */
  async disconnectGoogleCalendar(): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(
      'DELETE',
      '/google-calendar/disconnect',
      { workspaceId: this.workspaceId }
    );
  }

  /**
   * Get Google Calendar connection status
   */
  async getGoogleCalendarStatus(): Promise<{
    connected: boolean;
    configured: boolean;
    connectedAt?: string;
    calendarId?: string;
  }> {
    return this.request<{
      success: boolean;
      connected: boolean;
      configured: boolean;
      connectedAt?: string;
      calendarId?: string;
    }>(
      'GET',
      '/google-calendar/status',
      undefined,
      { workspaceId: this.workspaceId }
    );
  }

  /**
   * Create a calendar invite for a prioritization activity
   */
  async createCalendarInvite(params: {
    taskId: string;
    taskName: string;
    toolName: string;
    description?: string;
    scheduledStart: Date;
    durationMinutes?: number;
    participantUserIds: string[];
    meetingLink?: string;
  }): Promise<{
    success: boolean;
    eventId?: string;
    eventLink?: string;
    attendeesInvited?: number;
    missingUsers?: string[];
    error?: string;
  }> {
    return this.request<{
      success: boolean;
      eventId?: string;
      eventLink?: string;
      attendeesInvited?: number;
      missingUsers?: string[];
      error?: string;
    }>(
      'POST',
      '/google-calendar/invite',
      {
        workspaceId: this.workspaceId,
        taskId: params.taskId,
        taskName: params.taskName,
        toolName: params.toolName,
        description: params.description,
        scheduledStart: params.scheduledStart.toISOString(),
        durationMinutes: params.durationMinutes || 30,
        participantUserIds: params.participantUserIds,
        meetingLink: params.meetingLink,
      }
    );
  }

  /**
   * Cancel a calendar event
   */
  async cancelCalendarEvent(eventId: string): Promise<{ success: boolean; error?: string }> {
    return this.request<{ success: boolean; error?: string }>(
      'DELETE',
      `/google-calendar/event/${eventId}`,
      { workspaceId: this.workspaceId }
    );
  }

  /**
   * List available calendars
   */
  async listGoogleCalendars(): Promise<Array<{ id: string; summary: string; primary?: boolean }>> {
    const result = await this.request<{
      success: boolean;
      calendars: Array<{ id: string; summary: string; primary?: boolean }>;
    }>(
      'GET',
      '/google-calendar/calendars',
      undefined,
      { workspaceId: this.workspaceId }
    );
    return result.calendars || [];
  }

  // ==========================================================================
  // Zoom Integration
  // ==========================================================================

  /**
   * Initiate Zoom OAuth flow
   */
  async initiateZoomAuth(params: {
    userId: string;
    redirectUrl?: string;
  }): Promise<{ success: boolean; authUrl?: string; error?: string }> {
    return this.request<{ success: boolean; authUrl?: string; error?: string }>(
      'POST',
      '/zoom/connect',
      {
        workspaceId: this.workspaceId,
        userId: params.userId,
        redirectUrl: params.redirectUrl,
      }
    );
  }

  /**
   * Disconnect Zoom
   */
  async disconnectZoom(): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(
      'DELETE',
      '/zoom/disconnect',
      { workspaceId: this.workspaceId }
    );
  }

  /**
   * Get Zoom connection status
   */
  async getZoomStatus(): Promise<ZoomConnectionStatus> {
    return this.request<ZoomConnectionStatus & { success: boolean }>(
      'GET',
      '/zoom/status',
      undefined,
      { workspaceId: this.workspaceId }
    );
  }

  /**
   * Create a Zoom meeting for a prioritization activity
   */
  async createZoomMeeting(params: ZoomMeetingCreateParams): Promise<ZoomMeetingResult> {
    return this.request<ZoomMeetingResult>(
      'POST',
      '/zoom/meetings',
      {
        workspaceId: this.workspaceId,
        taskId: params.taskId,
        taskName: params.taskName,
        toolName: params.toolName,
        description: params.description,
        scheduledStart: params.scheduledStart.toISOString(),
        durationMinutes: params.durationMinutes ?? 30,
        participantEmails: params.participantEmails ?? [],
        waitingRoom: params.waitingRoom ?? true,
        autoRecord: params.autoRecord ?? false,
      }
    );
  }

  /**
   * List Zoom meetings
   */
  async listZoomMeetings(options?: {
    type?: 'scheduled' | 'live' | 'upcoming';
    pageSize?: number;
  }): Promise<ZoomMeeting[]> {
    const result = await this.request<{ success: boolean; meetings: ZoomMeeting[] }>(
      'GET',
      '/zoom/meetings',
      undefined,
      {
        workspaceId: this.workspaceId,
        ...(options?.type && { type: options.type }),
        ...(options?.pageSize && { pageSize: String(options.pageSize) }),
      }
    );
    return result.meetings || [];
  }

  /**
   * Delete/cancel a Zoom meeting
   */
  async deleteZoomMeeting(meetingId: string): Promise<{ success: boolean; error?: string }> {
    return this.request<{ success: boolean; error?: string }>(
      'DELETE',
      `/zoom/meetings/${meetingId}`,
      { workspaceId: this.workspaceId }
    );
  }

  /**
   * List Zoom cloud recordings, flagging which have a transcript / chat to ingest.
   */
  async listZoomRecordings(options?: { from?: string; to?: string; pageSize?: number }): Promise<ZoomRecordingSummary[]> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (options?.from) params.from = options.from;
    if (options?.to) params.to = options.to;
    if (options?.pageSize) params.pageSize = String(options.pageSize);
    const result = await this.request<{ success: boolean; recordings: ZoomRecordingSummary[] }>(
      'GET', '/zoom/recordings', undefined, params
    );
    return result.recordings || [];
  }

  /** Fetch a parsed Zoom transcript for a meeting. */
  async getZoomTranscript(meetingId: string): Promise<MeetingTranscript> {
    const result = await this.request<{ success: boolean; transcript: MeetingTranscript }>(
      'GET', `/zoom/meetings/${meetingId}/transcript`, undefined, { workspaceId: this.workspaceId }
    );
    return result.transcript;
  }

  /** Fetch Zoom in-meeting chat for a meeting. */
  async getZoomChat(meetingId: string): Promise<MeetingChatMessage[]> {
    const result = await this.request<{ success: boolean; chat: MeetingChatMessage[] }>(
      'GET', `/zoom/meetings/${meetingId}/chat`, undefined, { workspaceId: this.workspaceId }
    );
    return result.chat || [];
  }

  /** Import a Zoom meeting's transcript + chat as items. */
  async importZoomMeeting(meetingId: string, options?: {
    mode?: 'summary' | 'messages';
    include?: { transcript?: boolean; chat?: boolean };
  }): Promise<MeetingImportResult> {
    return this.request<MeetingImportResult>(
      'POST', '/zoom/import',
      { workspaceId: this.workspaceId, meetingId, mode: options?.mode, include: options?.include }
    );
  }

  // ==========================================================================
  // Microsoft Teams Integration (transcript / chat ingestion)
  // ==========================================================================

  /** Start Microsoft Teams OAuth (Entra ID); returns an authUrl to redirect to. */
  async initiateMicrosoftTeamsAuth(params: {
    userId: string;
    redirectUrl?: string;
  }): Promise<{ success: boolean; authUrl?: string; error?: string }> {
    return this.request<{ success: boolean; authUrl?: string; error?: string }>(
      'POST',
      '/microsoft-teams/connect',
      { workspaceId: this.workspaceId, userId: params.userId, redirectUrl: params.redirectUrl }
    );
  }

  async disconnectMicrosoftTeams(): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(
      'DELETE', '/microsoft-teams/disconnect', { workspaceId: this.workspaceId }
    );
  }

  async getMicrosoftTeamsStatus(): Promise<MicrosoftTeamsConnectionStatus> {
    return this.request<MicrosoftTeamsConnectionStatus & { success: boolean }>(
      'GET', '/microsoft-teams/status', undefined, { workspaceId: this.workspaceId }
    );
  }

  /** List Teams meetings that have a transcript available to ingest. */
  async listMicrosoftTeamsMeetings(options?: { top?: number }): Promise<TeamsMeetingTranscriptRef[]> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (options?.top) params.top = String(options.top);
    const result = await this.request<{ success: boolean; meetings: TeamsMeetingTranscriptRef[] }>(
      'GET', '/microsoft-teams/meetings', undefined, params
    );
    return result.meetings || [];
  }

  async getMicrosoftTeamsTranscript(meetingId: string, transcriptId?: string): Promise<MeetingTranscript> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (transcriptId) params.transcriptId = transcriptId;
    const result = await this.request<{ success: boolean; transcript: MeetingTranscript }>(
      'GET', `/microsoft-teams/meetings/${meetingId}/transcript`, undefined, params
    );
    return result.transcript;
  }

  async getMicrosoftTeamsChat(meetingId: string): Promise<MeetingChatMessage[]> {
    const result = await this.request<{ success: boolean; chat: MeetingChatMessage[] }>(
      'GET', `/microsoft-teams/meetings/${meetingId}/chat`, undefined, { workspaceId: this.workspaceId }
    );
    return result.chat || [];
  }

  async importMicrosoftTeamsMeeting(meetingId: string, options?: {
    mode?: 'summary' | 'messages';
    transcriptId?: string;
    include?: { transcript?: boolean; chat?: boolean };
  }): Promise<MeetingImportResult> {
    return this.request<MeetingImportResult>(
      'POST', '/microsoft-teams/import',
      {
        workspaceId: this.workspaceId,
        meetingId,
        transcriptId: options?.transcriptId,
        mode: options?.mode,
        include: options?.include,
      }
    );
  }

  // ==========================================================================
  // Google Meet Integration
  // ==========================================================================

  /**
   * Initiate Google Meet OAuth flow (uses Google Calendar API with Meet scope)
   */
  async initiateGoogleMeetAuth(params: {
    userId: string;
    redirectUrl?: string;
  }): Promise<{ success: boolean; authUrl?: string; error?: string }> {
    return this.request<{ success: boolean; authUrl?: string; error?: string }>(
      'POST',
      '/google-meet/connect',
      {
        workspaceId: this.workspaceId,
        userId: params.userId,
        redirectUrl: params.redirectUrl,
      }
    );
  }

  /**
   * Disconnect Google Meet
   */
  async disconnectGoogleMeet(): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(
      'DELETE',
      '/google-meet/disconnect',
      { workspaceId: this.workspaceId }
    );
  }

  /**
   * Get Google Meet connection status
   */
  async getGoogleMeetStatus(): Promise<GoogleMeetConnectionStatus> {
    return this.request<GoogleMeetConnectionStatus & { success: boolean }>(
      'GET',
      '/google-meet/status',
      undefined,
      { workspaceId: this.workspaceId }
    );
  }

  /**
   * Create a Google Meet meeting for a prioritization activity
   */
  async createGoogleMeetMeeting(params: GoogleMeetMeetingCreateParams): Promise<GoogleMeetMeetingResult> {
    return this.request<GoogleMeetMeetingResult>(
      'POST',
      '/google-meet/meetings',
      {
        workspaceId: this.workspaceId,
        taskId: params.taskId,
        taskName: params.taskName,
        toolName: params.toolName,
        description: params.description,
        scheduledStart: params.scheduledStart.toISOString(),
        durationMinutes: params.durationMinutes ?? 30,
        participantEmails: params.participantEmails ?? [],
        calendarId: params.calendarId,
      }
    );
  }

  /**
   * Cancel a Google Meet event
   */
  async cancelGoogleMeetEvent(eventId: string): Promise<{ success: boolean; error?: string }> {
    return this.request<{ success: boolean; error?: string }>(
      'DELETE',
      `/google-meet/events/${eventId}`,
      { workspaceId: this.workspaceId }
    );
  }

  /** List past Google Meet conference records (meetings with possible transcripts). */
  async listGoogleMeetConferences(options?: { pageSize?: number }): Promise<GoogleMeetConferenceRecord[]> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (options?.pageSize) params.pageSize = String(options.pageSize);
    const result = await this.request<{ success: boolean; conferences: GoogleMeetConferenceRecord[] }>(
      'GET', '/google-meet/conferences', undefined, params
    );
    return result.conferences || [];
  }

  /** Fetch a structured Google Meet transcript for a conference record. */
  async getGoogleMeetTranscript(conferenceRecordId: string): Promise<MeetingTranscript> {
    const result = await this.request<{ success: boolean; transcript: MeetingTranscript }>(
      'GET', `/google-meet/conferences/${conferenceRecordId}/transcript`, undefined, { workspaceId: this.workspaceId }
    );
    return result.transcript;
  }

  /** Import a Google Meet conference's transcript as items. */
  async importGoogleMeetConference(conferenceRecordId: string, options?: {
    mode?: 'summary' | 'entries';
  }): Promise<MeetingImportResult> {
    return this.request<MeetingImportResult>(
      'POST', '/google-meet/import',
      { workspaceId: this.workspaceId, conferenceRecordId, mode: options?.mode }
    );
  }

  // ==========================================================================
  // Granola Integration
  // ==========================================================================

  async connectGranola(apiKey: string): Promise<{ success: boolean; userName?: string; error?: string }> {
    return this.request<{ success: boolean; userName?: string; error?: string }>(
      'POST',
      '/granola/connect',
      { workspaceId: this.workspaceId, apiKey }
    );
  }

  async disconnectGranola(): Promise<void> {
    await this.request('DELETE', '/granola/disconnect', { workspaceId: this.workspaceId });
  }

  async getGranolaStatus(): Promise<GranolaConnectionStatus> {
    const result = await this.request<GranolaConnectionStatus & { success: boolean }>(
      'GET',
      '/granola/status',
      { workspaceId: this.workspaceId }
    );
    return { connected: result.connected, connectedAt: result.connectedAt };
  }

  async searchGranolaNotes(options?: {
    search?: string;
    createdAfter?: string;
    cursor?: string;
    limit?: number;
  }): Promise<{ notes: GranolaNote[]; hasMore: boolean; cursor?: string }> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (options?.search) params.search = options.search;
    if (options?.createdAfter) params.created_after = options.createdAfter;
    if (options?.cursor) params.cursor = options.cursor;
    if (options?.limit) params.limit = String(options.limit);

    const result = await this.request<{ success: boolean; notes: GranolaNote[]; hasMore: boolean; cursor?: string }>(
      'GET',
      '/granola/notes',
      params
    );
    return { notes: result.notes || [], hasMore: result.hasMore ?? false, cursor: result.cursor };
  }

  // ==========================================================================
  // Figma Integration
  // ==========================================================================

  async connectFigma(accessToken: string, teamId?: string): Promise<{ success: boolean; userName?: string; email?: string; error?: string }> {
    return this.request<{ success: boolean; userName?: string; email?: string; error?: string }>(
      'POST', '/figma/connect',
      { workspaceId: this.workspaceId, accessToken, teamId }
    );
  }

  async disconnectFigma(): Promise<void> {
    await this.request('DELETE', '/figma/disconnect', { workspaceId: this.workspaceId });
  }

  async getFigmaStatus(): Promise<FigmaConnectionStatus> {
    const result = await this.request<FigmaConnectionStatus & { success: boolean }>(
      'GET', '/figma/status', { workspaceId: this.workspaceId }
    );
    return { connected: result.connected, connectedAt: result.connectedAt, teamId: result.teamId };
  }

  async listFigmaProjects(teamId: string): Promise<FigmaProject[]> {
    const result = await this.request<{ success: boolean; projects: FigmaProject[] }>(
      'GET', '/figma/projects', { workspaceId: this.workspaceId, teamId }
    );
    return result.projects || [];
  }

  async listFigmaProjectFiles(projectId: string): Promise<FigmaFile[]> {
    const result = await this.request<{ success: boolean; files: FigmaFile[] }>(
      'GET', `/figma/projects/${projectId}/files`, { workspaceId: this.workspaceId }
    );
    return result.files || [];
  }

  async getFigmaFile(fileKey: string): Promise<{ name: string; lastModified: string; pages: FigmaPage[]; frameCount: number }> {
    const result = await this.request<{ success: boolean; file: { name: string; lastModified: string; pages: FigmaPage[]; frameCount: number } }>(
      'GET', `/figma/files/${fileKey}`, { workspaceId: this.workspaceId }
    );
    return result.file;
  }

  async listFigmaFrames(fileKey: string, options?: { pageId?: string; limit?: number }): Promise<FigmaFrame[]> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (options?.pageId) params.pageId = options.pageId;
    if (options?.limit) params.limit = String(options.limit);
    const result = await this.request<{ success: boolean; frames: FigmaFrame[] }>(
      'GET', `/figma/files/${fileKey}/frames`, params
    );
    return result.frames || [];
  }

  async previewFigmaImport(fileKey: string, options?: { pageId?: string }): Promise<{
    frames: FigmaFrame[];
    totalCount: number;
    pageBreakdown: { pageId: string; pageName: string; count: number }[];
  }> {
    const result = await this.request<{
      success: boolean;
      frames: FigmaFrame[];
      totalCount: number;
      pageBreakdown: { pageId: string; pageName: string; count: number }[];
    }>('POST', '/figma/preview', { workspaceId: this.workspaceId, fileKey, pageId: options?.pageId });
    return { frames: result.frames || [], totalCount: result.totalCount || 0, pageBreakdown: result.pageBreakdown || [] };
  }

  async importFigmaFrames(fileKey: string, options?: { frameIds?: string[]; pageId?: string }): Promise<{ items: FigmaImportItem[]; count: number }> {
    const result = await this.request<{ success: boolean; items: FigmaImportItem[]; count: number }>(
      'POST', '/figma/import',
      { workspaceId: this.workspaceId, fileKey, frameIds: options?.frameIds, pageId: options?.pageId }
    );
    return { items: result.items || [], count: result.count || 0 };
  }

  async exportToFigma(fileKey: string, priorities: Array<{ id: string; title: string; description?: string; rank?: number }>): Promise<{ posted: number; errors: string[] }> {
    const result = await this.request<{ success: boolean; posted: number; errors: string[] }>(
      'POST', '/figma/export',
      { workspaceId: this.workspaceId, fileKey, priorities }
    );
    return { posted: result.posted || 0, errors: result.errors || [] };
  }

  // ==========================================================================
  // Dropbox Integration (content data source — OAuth)
  // ==========================================================================

  /**
   * Begin the Dropbox OAuth flow. Returns an authorization URL to redirect to.
   * Pass `scope: 'user'` + `userId` to connect a PERSONAL credential (My
   * Connectors); the default 'workspace' connects the workspace-shared one.
   */
  async initiateDropboxAuth(params?: { userId?: string; redirectUrl?: string; scope?: 'workspace' | 'user' }): Promise<{ success: boolean; authUrl?: string; error?: string }> {
    return this.request<{ success: boolean; authUrl?: string; error?: string }>(
      'POST', '/dropbox/connect',
      {
        workspaceId: this.workspaceId,
        userId: params?.userId || 'current-user',
        redirectUrl: params?.redirectUrl ?? (typeof window !== 'undefined' ? window.location.href : undefined),
        scope: params?.scope ?? 'workspace',
      }
    );
  }

  /** Pass `userId` to disconnect a personal credential; omit it for the workspace-shared one. */
  async disconnectDropbox(userId?: string): Promise<void> {
    await this.request('DELETE', '/dropbox/disconnect', { workspaceId: this.workspaceId, userId });
  }

  /** Pass `userId` to read the personal credential's status; omit it for the workspace-shared one. */
  async getDropboxStatus(userId?: string): Promise<KnowledgeConnectionStatus> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (userId) params.userId = userId;
    return this.request<KnowledgeConnectionStatus & { success: boolean }>(
      'GET', '/dropbox/status', undefined, params
    );
  }

  async listDropboxFiles(options?: { path?: string; limit?: number; userId?: string }): Promise<DropboxFile[]> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (options?.path) params.path = options.path;
    if (options?.limit) params.limit = String(options.limit);
    if (options?.userId) params.userId = options.userId;
    const result = await this.request<{ success: boolean; files: DropboxFile[] }>(
      'GET', '/dropbox/files', undefined, params
    );
    return result.files || [];
  }

  async searchDropboxFiles(query: string, options?: { limit?: number; userId?: string }): Promise<DropboxFile[]> {
    const params: Record<string, string> = { workspaceId: this.workspaceId, query };
    if (options?.limit) params.limit = String(options.limit);
    if (options?.userId) params.userId = options.userId;
    const result = await this.request<{ success: boolean; files: DropboxFile[] }>(
      'GET', '/dropbox/search', undefined, params
    );
    return result.files || [];
  }

  async readDropboxFile(fileId: string, userId?: string): Promise<DocumentContent> {
    const params: Record<string, string> = { workspaceId: this.workspaceId, fileId };
    if (userId) params.userId = userId;
    const result = await this.request<{ success: boolean; id: string; name: string; text: string }>(
      'GET', '/dropbox/content', undefined, params
    );
    return { id: result.id, name: result.name, text: result.text };
  }

  // ==========================================================================
  // Google Docs Integration (content data source — OAuth)
  // ==========================================================================

  /**
   * Begin the Google Docs OAuth flow. Returns an authorization URL to redirect to.
   * Pass `scope: 'user'` + `userId` to connect a PERSONAL credential (My
   * Connectors); the default 'workspace' connects the workspace-shared one.
   */
  async initiateGoogleDocsAuth(params?: { userId?: string; redirectUrl?: string; scope?: 'workspace' | 'user' }): Promise<{ success: boolean; authUrl?: string; error?: string }> {
    return this.request<{ success: boolean; authUrl?: string; error?: string }>(
      'POST', '/google-docs/connect',
      {
        workspaceId: this.workspaceId,
        userId: params?.userId || 'current-user',
        redirectUrl: params?.redirectUrl ?? (typeof window !== 'undefined' ? window.location.href : undefined),
        scope: params?.scope ?? 'workspace',
      }
    );
  }

  /** Pass `userId` to disconnect a personal credential; omit it for the workspace-shared one. */
  async disconnectGoogleDocs(userId?: string): Promise<void> {
    await this.request('DELETE', '/google-docs/disconnect', { workspaceId: this.workspaceId, userId });
  }

  /** Pass `userId` to read the personal credential's status; omit it for the workspace-shared one. */
  async getGoogleDocsStatus(userId?: string): Promise<KnowledgeConnectionStatus> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (userId) params.userId = userId;
    return this.request<KnowledgeConnectionStatus & { success: boolean }>(
      'GET', '/google-docs/status', undefined, params
    );
  }

  async listGoogleDocs(options?: { query?: string; limit?: number; userId?: string }): Promise<GoogleDocSummary[]> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (options?.query) params.query = options.query;
    if (options?.limit) params.limit = String(options.limit);
    if (options?.userId) params.userId = options.userId;
    const result = await this.request<{ success: boolean; documents: GoogleDocSummary[] }>(
      'GET', '/google-docs/documents', undefined, params
    );
    return result.documents || [];
  }

  async readGoogleDoc(documentId: string, userId?: string): Promise<DocumentContent> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (userId) params.userId = userId;
    const result = await this.request<{ success: boolean; id: string; name: string; text: string }>(
      'GET', `/google-docs/documents/${encodeURIComponent(documentId)}`, undefined, params
    );
    return { id: result.id, name: result.name, text: result.text };
  }
  // ==========================================================================
  // Intercom Integration (support conversations)
  // ==========================================================================

  /**
   * Begin the Intercom OAuth flow. Returns an authorization URL the caller
   * redirects the browser to; the engine's /intercom/callback finishes the
   * exchange and persists the token.
   */
  async connectIntercom(
    region?: IntercomRegion,
    redirectUrl?: string,
  ): Promise<{ success: boolean; authUrl?: string; error?: string }> {
    return this.request<{ success: boolean; authUrl?: string; error?: string }>(
      'POST',
      '/intercom/connect',
      { workspaceId: this.workspaceId, region, redirectUrl },
    );
  }

  async disconnectIntercom(): Promise<void> {
    await this.request('DELETE', '/intercom/disconnect', { workspaceId: this.workspaceId });
  }

  async getIntercomStatus(): Promise<IntercomConnectionStatus> {
    const result = await this.request<IntercomConnectionStatus & { success: boolean }>(
      'GET',
      '/intercom/status',
      undefined,
      { workspaceId: this.workspaceId },
    );
    return {
      connected: result.connected,
      connectedAt: result.connectedAt,
      workspaceName: result.workspaceName,
      region: result.region,
      configured: result.configured,
    };
  }

  async listIntercomConversations(options?: {
    search?: string;
    state?: 'open' | 'closed' | 'snoozed';
    startingAfter?: string;
    limit?: number;
  }): Promise<{ conversations: IntercomConversation[]; hasMore: boolean; startingAfter?: string }> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (options?.search) params.search = options.search;
    if (options?.state) params.state = options.state;
    if (options?.startingAfter) params.starting_after = options.startingAfter;
    if (options?.limit) params.limit = String(options.limit);

    const result = await this.request<{
      success: boolean;
      conversations: IntercomConversation[];
      hasMore: boolean;
      startingAfter?: string;
    }>('GET', '/intercom/conversations', undefined, params);
    return {
      conversations: result.conversations || [],
      hasMore: result.hasMore ?? false,
      startingAfter: result.startingAfter,
    };
  }

  async getIntercomConversation(
    conversationId: string,
  ): Promise<IntercomConversation & { transcript?: string }> {
    const result = await this.request<{
      success: boolean;
      conversation: IntercomConversation & { transcript?: string };
    }>('GET', `/intercom/conversations/${conversationId}`, undefined, { workspaceId: this.workspaceId });
    return result.conversation;
  }

  // ==========================================================================
  // Zendesk Integration (support tickets)
  // ==========================================================================

  /**
   * Begin the Zendesk OAuth flow. Returns an authorization URL the caller
   * redirects the browser to; the engine's /zendesk/callback finishes the
   * exchange and persists the access + refresh tokens.
   */
  async connectZendesk(
    subdomain: string,
    redirectUrl?: string,
  ): Promise<{ success: boolean; authUrl?: string; error?: string }> {
    return this.request<{ success: boolean; authUrl?: string; error?: string }>(
      'POST',
      '/zendesk/connect',
      { workspaceId: this.workspaceId, subdomain, redirectUrl },
    );
  }

  async disconnectZendesk(): Promise<void> {
    await this.request('DELETE', '/zendesk/disconnect', { workspaceId: this.workspaceId });
  }

  async getZendeskStatus(): Promise<ZendeskConnectionStatus> {
    const result = await this.request<ZendeskConnectionStatus & { success: boolean }>(
      'GET',
      '/zendesk/status',
      undefined,
      { workspaceId: this.workspaceId },
    );
    return {
      connected: result.connected,
      connectedAt: result.connectedAt,
      subdomain: result.subdomain,
      userName: result.userName,
      configured: result.configured,
    };
  }

  async listZendeskTickets(options?: {
    search?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ tickets: ZendeskTicket[]; hasMore: boolean; nextPage?: number }> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (options?.search) params.search = options.search;
    if (options?.status) params.status = options.status;
    if (options?.page) params.page = String(options.page);
    if (options?.limit) params.limit = String(options.limit);

    const result = await this.request<{
      success: boolean;
      tickets: ZendeskTicket[];
      hasMore: boolean;
      nextPage?: number;
    }>('GET', '/zendesk/tickets', undefined, params);
    return { tickets: result.tickets || [], hasMore: result.hasMore ?? false, nextPage: result.nextPage };
  }

  async getZendeskTicket(ticketId: string): Promise<ZendeskTicket & { transcript?: string }> {
    const result = await this.request<{
      success: boolean;
      ticket: ZendeskTicket & { transcript?: string };
    }>('GET', `/zendesk/tickets/${ticketId}`, undefined, { workspaceId: this.workspaceId });
    return result.ticket;
  }

  // ==========================================================================
  // Salesforce Integration (CRM opportunities)
  // ==========================================================================

  /**
   * Begin the Salesforce OAuth Web Server flow. Returns an authorization URL the
   * caller redirects the browser to; the engine's /salesforce/callback finishes
   * the exchange and persists the access + refresh tokens and instance URL.
   * `loginUrl` lets a sandbox authorize against test.salesforce.com.
   */
  async connectSalesforce(
    loginUrl?: string,
    redirectUrl?: string,
  ): Promise<{ success: boolean; authUrl?: string; error?: string }> {
    return this.request<{ success: boolean; authUrl?: string; error?: string }>(
      'POST',
      '/salesforce/connect',
      { workspaceId: this.workspaceId, loginUrl, redirectUrl },
    );
  }

  async disconnectSalesforce(): Promise<void> {
    await this.request('DELETE', '/salesforce/disconnect', { workspaceId: this.workspaceId });
  }

  async getSalesforceStatus(): Promise<SalesforceConnectionStatus> {
    const result = await this.request<SalesforceConnectionStatus & { success: boolean }>(
      'GET',
      '/salesforce/status',
      undefined,
      { workspaceId: this.workspaceId },
    );
    return {
      connected: result.connected,
      connectedAt: result.connectedAt,
      instanceUrl: result.instanceUrl,
      configured: result.configured,
    };
  }

  async listSalesforceOpportunities(options?: {
    search?: string;
    stages?: string[];
    openOnly?: boolean;
    includeFeatureRequest?: boolean;
    limit?: number;
  }): Promise<{ opportunities: SalesforceOpportunity[]; totalSize: number }> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (options?.search) params.search = options.search;
    if (options?.stages && options.stages.length) params.stages = options.stages.join(',');
    if (options?.openOnly) params.openOnly = 'true';
    if (options?.includeFeatureRequest) params.includeFeatureRequest = 'true';
    if (options?.limit) params.limit = String(options.limit);

    const result = await this.request<{
      success: boolean;
      opportunities: SalesforceOpportunity[];
      totalSize: number;
    }>('GET', '/salesforce/opportunities', undefined, params);
    return { opportunities: result.opportunities || [], totalSize: result.totalSize ?? 0 };
  }

  async getSalesforceOpportunity(opportunityId: string): Promise<SalesforceOpportunity> {
    const result = await this.request<{
      success: boolean;
      opportunity: SalesforceOpportunity;
    }>('GET', `/salesforce/opportunities/${opportunityId}`, undefined, { workspaceId: this.workspaceId });
    return result.opportunity;
  }

  // ==========================================================================
  // Datadog Integration (import — reliability evidence)
  // ==========================================================================

  async connectDatadog(apiKey: string, appKey: string, site?: string): Promise<{ success: boolean; error?: string }> {
    return this.request<{ success: boolean; error?: string }>(
      'POST', '/datadog/connect',
      { workspaceId: this.workspaceId, apiKey, appKey, site }
    );
  }

  async disconnectDatadog(): Promise<void> {
    await this.request('DELETE', '/datadog/disconnect', { workspaceId: this.workspaceId });
  }

  async getDatadogStatus(): Promise<DatadogConnectionStatus> {
    const result = await this.request<DatadogConnectionStatus & { success: boolean }>(
      'GET', '/datadog/status', undefined, { workspaceId: this.workspaceId }
    );
    return { connected: result.connected, connectedAt: result.connectedAt, site: result.site };
  }

  async getDatadogReliabilitySignals(options?: {
    sloQuery?: string;
    sloLimit?: number;
    monitorQuery?: string;
  }): Promise<DatadogReliabilitySignals> {
    const params: Record<string, string> = { workspaceId: this.workspaceId };
    if (options?.sloQuery) params.sloQuery = options.sloQuery;
    if (options?.sloLimit) params.sloLimit = String(options.sloLimit);
    if (options?.monitorQuery) params.monitorQuery = options.monitorQuery;
    const result = await this.request<{ success: boolean; signals: DatadogReliabilitySignals }>(
      'GET', '/datadog/reliability-signals', undefined, params
    );
    return result.signals;
  }

  async listDatadogIncidents(limit = 50): Promise<{ incidents: DatadogIncident[]; count: number }> {
    const result = await this.request<{ success: boolean; incidents: DatadogIncident[]; count: number }>(
      'GET', '/datadog/incidents', undefined, { workspaceId: this.workspaceId, limit: String(limit) }
    );
    return { incidents: result.incidents || [], count: result.count || 0 };
  }

  // ==========================================================================
  // Snowflake Integration (import — warehouse enrichment, read-only)
  // ==========================================================================

  async connectSnowflake(creds: {
    account: string;
    username: string;
    token: string;
    warehouse?: string;
    database?: string;
    schema?: string;
    role?: string;
  }): Promise<{ success: boolean; version?: string; account?: string; user?: string; error?: string }> {
    return this.request<{ success: boolean; version?: string; account?: string; user?: string; error?: string }>(
      'POST', '/snowflake/connect',
      { workspaceId: this.workspaceId, ...creds }
    );
  }

  async disconnectSnowflake(): Promise<void> {
    await this.request('DELETE', '/snowflake/disconnect', { workspaceId: this.workspaceId });
  }

  async getSnowflakeStatus(): Promise<SnowflakeConnectionStatus> {
    const result = await this.request<SnowflakeConnectionStatus & { success: boolean }>(
      'GET', '/snowflake/status', undefined, { workspaceId: this.workspaceId }
    );
    return {
      connected: result.connected,
      connectedAt: result.connectedAt,
      account: result.account,
      warehouse: result.warehouse,
      database: result.database,
    };
  }

  /** Run a single read-only warehouse query. The engine rejects writes/DDL. */
  async runSnowflakeQuery(statement: string, options?: { limit?: number; timeoutSeconds?: number }): Promise<SnowflakeQueryResult> {
    const result = await this.request<SnowflakeQueryResult & { success: boolean }>(
      'POST', '/snowflake/query',
      { workspaceId: this.workspaceId, statement, limit: options?.limit, timeoutSeconds: options?.timeoutSeconds }
    );
    return { columns: result.columns || [], rows: result.rows || [], rowCount: result.rowCount || 0, statementHandle: result.statementHandle };
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

let apiInstance: IntegrationsApiClient | null = null;

/**
 * Workspace id for integrations-engine credential persist.
 * `connector_configs.workspace_id` is a UUID FK to `workspaces` — page/list
 * sentinels (`intake`, `items-library`, `default`) fail the insert and the
 * UI surfaces that as "Failed to connect to Miro".
 */
export function resolveIntegrationsWorkspaceId(
  ...candidates: Array<string | null | undefined>
): string | null {
  for (const id of candidates) {
    if (isUuid(id)) return id;
  }
  return null;
}

export function getIntegrationsApi(workspaceId: string = 'default'): IntegrationsApiClient {
  if (!apiInstance || apiInstance['workspaceId'] !== workspaceId) {
    apiInstance = new IntegrationsApiClient(workspaceId);
  }
  return apiInstance;
}

export { IntegrationsApiClient };
